import { memo, useRef, useState, useEffect, useContext } from "react";
import classNames from "classnames";
import ProductList from "app/pages/Listing/ProductList/ProductList.jsx";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import PropTypes from "prop-types";
import { merchandisingShape, merchandisingTopic } from "app/utils/propTypes";
import get from "lodash/get";
import "./MerchandisingListing.scss";
import MerchandisingFilters from "app/pages/Merchandising/MerchandisingFilters/MerchandisingFilters";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import Loader from "app/pages/.shared/Loader";
import MerchandisingMedias from "app/pages/Merchandising/Media/MerchandisingMedias";
import { FormattedMessage, useIntl } from "react-intl";
import { BreadcrumbsContainer } from "app/features/breadcrumb";
import Paragraphs from "app/pages/.shared/Paragraphs";
import PartnerSEO from "app/pages/.shared/PartnerSEO/PartnerSEO";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import MerchListingActionbar from "app/pages/.shared/MerchListingActionbar/MerchListingActionbar";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";
import ReasonToBookBlockContainer from "app/pages/.shared/ReasonsToBook/ReasonToBookBlock/ReasonToBookBlockContainer";
import MerchandisingListingHeadMetadata from "app/pages/Merchandising/MerchandisingListing/MerchandisingListingHeadMetadata";
import MerchandisingMediasIntroContainer from "app/pages/Merchandising/Media/MerchandisingMediasIntroContainer";
import Separator from "app/pages/.shared/Separator";
import SimpleProgressiveImg from "app/pages/.shared/SimpleProgressiveImg";
import ScrollToTopButton from "app/pages/.shared/ScrollToTopButton/ScrollToTopButton";
import { ButtonLink } from "app/components/ui/ButtonLink/ButtonLink";
import WebsiteTunnelSwitcherContainer from "app/pages/.shared/WebsiteTunnelSwitcher/WebsiteTunnelSwitcherContainer";
import { useFacets } from "app/features/product/productFilters";
import PaymentDescriptionContainer from "app/pages/.shared/DynamicBlocks/blocks/PaymentDescriptionBlock/PaymentDescriptionContainer";
import { useIntersectionObserver } from "app/hooks";
import { ActionBar } from "app/features/product/productActionBar/ActionBar";
import { useSortedProducts } from "app/features/product/productSort";
import AppGlobalsContext from "app/AppGlobalsContext";
import MerchandisingHero from "app/pages/Merchandising/MerchandisingHero/MerchandisingHero";

const MEDIA_IMG_DESKTOP_SIZES = {
	medium: { width: 1440 },
	large: { width: 1440 },
	xlarge: { width: 1440 },
};

const MEDIA_LOGO_SIZES = {
	small: { width: 450 },
	medium: { width: 450 },
	large: { width: 450 },
	xlarge: { width: 450 },
};

const IMG_MOBILE_SIZES = {
	small: {},
	medium: {},
};

const PRODUCTS_PER_CHUNK = 12;

const MerchandisingListing = ({
	resolution,
	currentMerchandising = {},
	mediaIntroduction = {},
	merchandisingTopics = [],
	merchandisingMedias = [],
	shouldHideProductPriceBanner, // // TODO temporaire voir FBR-2686
	activeFilter,
	loading,
	listingSEO = {},
	isPaymentDescriptionBlockExist,
	websiteTunnels,
}) => {
	const actionBarRef = useRef();
	const topOfProductListRef = useRef();

	const { track } = useAnalytics();
	const { formatMessage } = useIntl();
	const { partnerCode } = useContext(AppGlobalsContext);

	const isLargeResolution = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const currentMerchandisingFooter = currentMerchandising.footer || {};
	const {
		activateRedirect = false,
		labelRedirectListing = <FormattedMessage id="merchandising.footer.redirect.cta.text" />,
		positionRedirectListing = "center",
	} = currentMerchandisingFooter;

	const merchandisingPictureUrl = isLargeResolution
		? currentMerchandising?.mainPhotoLarge?.url
		: currentMerchandising?.mainPhotoSmall?.url;

	const merchandisingPictureThumbnail = isLargeResolution
		? currentMerchandising?.mainPhotoLarge?.thumbnail
		: currentMerchandising?.mainPhotoSmall?.thumbnail;

	const hasMedia = currentMerchandising.media;

	const mainPictureClassNames = classNames({
		"listing__merchandising-picture": true,
		"listing__merchandising-picture--media": hasMedia,
	});

	const smartDP = currentMerchandising?.displayOptions?.smartDP;
	const hideLowestPrice = currentMerchandising?.displayOptions?.stamp?.hideLowestPrice;
	const isHaram = partnerCode.startsWith("SVUM");
	const introduction = currentMerchandising?.displayOptions?.introduction;
	const isHeroDisplayed = Boolean(merchandisingPictureUrl) && !hasMedia;

	const { filteredProducts, activeFilters } = useFacets();
	const { sortedProducts } = useSortedProducts(filteredProducts);

	const [loadMoreRef, loadMoreRefInView] = useIntersectionObserver({
		rootMargin: "0px 0px 600px 0px",
	});

	const [visibleProductsCount, setVisibleProductsCount] = useState(PRODUCTS_PER_CHUNK);
	const visibleProducts = sortedProducts?.slice(0, visibleProductsCount);
	const hasMoreToShow = sortedProducts?.length > visibleProductsCount;

	useEffect(() => {
		if (!loadMoreRefInView) {
			return;
		}

		if (hasMoreToShow) {
			setVisibleProductsCount(prev => prev + PRODUCTS_PER_CHUNK);
		}
	}, [loadMoreRefInView, hasMoreToShow]);

	useEffect(() => {
		setVisibleProductsCount(PRODUCTS_PER_CHUNK);
	}, [activeFilters]);

	const handleScrollToTop = () => {
		if (!topOfProductListRef.current) {
			return;
		}

		requestAnimationFrame(() => {
			topOfProductListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	};

	const handleSortChange = sortById => {
		track("sort_product_list", { sortby: sortById });
	};

	const handleFilterOpen = (filterId, isSticky) => {
		track(`listingPage_filter_by_${filterId}_open`, {
			...(!isMobile &&
				typeof isSticky === "boolean" && { filterPosition: isSticky ? "sticky" : "top" }),
		});
	};

	const handleMobileFilterOpen = isSticky => {
		track("listingPage_filters_mobile_open", {
			filterPosition: isSticky ? "sticky" : "top",
		});
	};

	const handleSortOpen = isSticky => {
		track("user_click_sort-button", {
			sortPosition: isSticky ? "sticky" : "top",
		});
	};

	const listingMerchandisingClassName = classNames({
		"listing--merchandising": true,
		"listing--merchandising__media": hasMedia,
		"listing--merchandising--no-payment-banner": !isPaymentDescriptionBlockExist,
		"listing--merchandising--no-header": !hasMedia,
	});
	const buttonClassName = classNames("listing__redirect-cta", {
		"listing__redirect-cta--center": positionRedirectListing === "center",
		"listing__redirect-cta--left": positionRedirectListing === "left",
		"listing__redirect-cta--right": positionRedirectListing === "right",
	});

	const crumbs = [
		{ label: formatMessage({ id: "faq.breadcrumb.home" }), to: "/" },
		{ label: mediaIntroduction?.title || smartDP?.merchandisingTitle || "" },
	];

	return (
		<div className={listingMerchandisingClassName} data-testid="listing-merchandising">
			<MerchandisingListingHeadMetadata merchandisingLabel={currentMerchandising.label} />

			{!isHaram && <BreadcrumbsContainer items={crumbs} className="listing__breadcrumbs" />}

			{isHeroDisplayed && (
				<MerchandisingHero
					title={smartDP?.merchandisingTitle}
					subtitle={smartDP?.merchandisingSubtitle}
					description={introduction}
					lowestPrice={currentMerchandising.lowestPrice}
					hideLowestPrice={hideLowestPrice}
					imageUrl={merchandisingPictureUrl}
					imageThumbnail={merchandisingPictureThumbnail}
					imageAlt={currentMerchandising?.label}
				>
					{smartDP?.showSmartDPForm && (
						<WebsiteTunnelSwitcherContainer
							websiteTunnels={websiteTunnels}
							disableFormPersistance={false}
						/>
					)}
				</MerchandisingHero>
			)}

			{hasMedia && (
				<>
					{merchandisingPictureUrl && (
						<div className={mainPictureClassNames} data-testid="main-picture">
							<div className="listing__container">
								{isLargeResolution ? (
									<BackgroundProgressiveImg
										sizes={MEDIA_IMG_DESKTOP_SIZES}
										src={merchandisingPictureUrl}
										thumbnail={merchandisingPictureThumbnail}
										resolution={resolution}
									/>
								) : (
									<BackgroundProgressiveImg
										sizes={IMG_MOBILE_SIZES}
										src={merchandisingPictureUrl}
										thumbnail={merchandisingPictureThumbnail}
										resolution={resolution}
									/>
								)}
								<div className="listing__merchandising-picture--media-icon" />

								{currentMerchandising?.media?.logo && (
									<div className="listing__merchandising-picture--media-logo">
										<SimpleProgressiveImg
											resolution={resolution}
											sizes={MEDIA_LOGO_SIZES}
											src={currentMerchandising?.media?.logo}
										/>
									</div>
								)}
							</div>
						</div>
					)}
					<div className="listing__merchandising-media">
						<MerchandisingMediasIntroContainer scrollTargetRef={actionBarRef} />
						<MerchandisingMedias
							resolution={resolution}
							articles={merchandisingMedias}
						/>
					</div>

					{isMobile && (
						<div className="listing__merchandising-media-separator">
							<Separator margin="narrow" design="full" />
						</div>
					)}
				</>
			)}

			{isPaymentDescriptionBlockExist && (
				<div className="listing__merchandising-payment-description" ref={actionBarRef}>
					<PaymentDescriptionContainer />
				</div>
			)}

			{hasMedia && (
				<div className="listing__merchandising-header" ref={actionBarRef}>
					<MerchListingActionbar
						label={currentMerchandising?.label}
						fromPrice={currentMerchandising?.lowestPrice}
						hideLowestPrice={hideLowestPrice}
						hidePaymentDescriptionBlock={isPaymentDescriptionBlockExist}
					/>
				</div>
			)}

			{introduction && !isHeroDisplayed && (
				<>
					{!isPaymentDescriptionBlockExist && hasMedia && (
						<Separator margin="narrow" design="full" />
					)}
					<div
						className={classNames("listing__introduction", {
							"listing__introduction--with-padding":
								currentMerchandising?.displayOptions?.hideFilters,
						})}
						data-testid="listing-introduction"
					>
						<Paragraphs paragraphs={introduction} enableHTMLFormatting />
					</div>
				</>
			)}

			{!get(currentMerchandising, "displayOptions.hideFilters") && (
				<>
					<div ref={topOfProductListRef} aria-hidden="true" />
					<ActionBar
						showMiniSearch={false}
						showMapButton={false}
						isLoading={false}
						onFilterTriggerMobileOpen={handleMobileFilterOpen}
						onFilterOpen={handleFilterOpen}
						onSubmit={handleScrollToTop}
						onSortOpen={handleSortOpen}
						onSortChange={handleSortChange}
					/>
				</>
			)}

			<div className="listing__merchandising-products">
				<div className="listing__container">
					{merchandisingTopics.length > 0 && (
						<div className="listing__merchandising-filters">
							<MerchandisingFilters
								activeFilter={activeFilter}
								merchandisingTopics={merchandisingTopics}
							/>
						</div>
					)}

					<div className="listing__product-list">
						<ProductList
							products={visibleProducts}
							hidePriceBanner={shouldHideProductPriceBanner}
							hideMembershipBlock
						/>
					</div>

					<div ref={loadMoreRef} aria-hidden="true" />

					{Boolean(activateRedirect) && (
						<div className="listing__redirect" data-testid="listing-redirect">
							<ButtonLink
								variant="secondary"
								size="lg"
								className={buttonClassName}
								data-testid="listing-redirect-cta"
								to={{ pathname: "/listing" }}
								onClick={() => track("merch_listing_redirect_cta_clicked")}
							>
								{labelRedirectListing}
							</ButtonLink>
						</div>
					)}

					{loading && (
						<div className="listing__loader">
							<Loader />
						</div>
					)}
				</div>
			</div>

			<PartnerSEO
				title={listingSEO.title}
				description={listingSEO.description}
				resolution={resolution}
			/>

			<ScrollToTopButton />

			<div className="listing__rtb">
				<div className="listing__container">
					<ReasonToBookBlockContainer />
				</div>
			</div>

			<PushNotificationContainer />
		</div>
	);
};

MerchandisingListing.propTypes = {
	loading: PropTypes.bool,
	activeFilter: PropTypes.string,
	resolution: PropTypes.string,
	merchandisingMedias: PropTypes.arrayOf(merchandisingShape),
	mediaIntroduction: PropTypes.object,
	currentMerchandising: merchandisingShape,
	shouldHideProductPriceBanner: PropTypes.bool,
	merchandisingTopics: PropTypes.arrayOf(merchandisingTopic),
	listingSEO: PropTypes.object,
	isPaymentDescriptionBlockExist: PropTypes.bool,
	websiteTunnels: PropTypes.array,
};

export default memo(MerchandisingListing);
